import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectUserPermissions, selectIsReadOnly } from '@/entities/layout';
import {
  getConsignmentPermissions,
  getConsignmentLinePermissions,
  type ConsignmentStatusCode
} from '@/entities/consignments';
import type { Permissions } from '@/entities/layout';

import { useProjectSiteParams } from './use-project-site-params';

export const usePermissions = () => {
  const { projectId, siteId } = useProjectSiteParams();

  // Get permissions for current project/site context
  const permissions = useSelector(
    projectId && siteId ? selectUserPermissions(Number(projectId), Number(siteId)) : () => undefined
  );

  const isReadOnly = useSelector(selectIsReadOnly);

  const checkPermission = useCallback(
    (permission: keyof Permissions): boolean => {
      if (!permissions) return false;
      return Boolean(permissions[permission]);
    },
    [permissions]
  );

  const checkRole = useCallback(
    (role: string): boolean => {
      if (!permissions?.roles) return false;
      return permissions.roles.includes(role);
    },
    [permissions]
  );

  // Memoize these values to avoid unnecessary recalculations
  const canAccessMaintenance = useMemo(() => permissions?.canAccessMaintenance || false, [permissions]);
  const isAdmin = useMemo(() => permissions?.admin || false, [permissions]);
  const isProjectAdmin = useMemo(() => permissions?.projectAdmin || false, [permissions]);
  const hasPRA = useMemo(() => checkRole('PRA'), [checkRole]);
  const hasADM = useMemo(() => checkRole('ADM'), [checkRole]);

  return {
    permissions,
    isReadOnly,
    isAdmin,
    isProjectAdmin,
    canAccessMaintenance,
    roles: permissions?.roles || [],
    checkPermission,
    checkRole,
    hasRole: checkRole, // alias for checkRole
    hasPRA,
    hasADM,

    // Derived permissions
    canManageUsers: isAdmin || isProjectAdmin,
    canViewReports: !isReadOnly,
    canExportData: !isReadOnly,
    canEditData: !isReadOnly,

    /**
     * Get permissions for consignment-level operations
     * Takes into account global read-only mode
     */
    useConsignmentPermissions: useCallback(
      (isConsignmentReadOnly: boolean) => {
        // Combine global read-only with consignment read-only
        const effectiveReadOnly = isReadOnly || isConsignmentReadOnly;
        return getConsignmentPermissions(effectiveReadOnly);
      },
      [isReadOnly]
    ),

    /**
     * Get permissions for consignment line operations
     * Combines business rules with user roles
     */
    useConsignmentLinePermissions: useCallback(
      (isConsignmentReadOnly: boolean, statusCode: ConsignmentStatusCode, hasFirstReceive: boolean = false) => {
        // Combine global read-only with consignment read-only
        const effectiveReadOnly = isReadOnly || isConsignmentReadOnly;

        return getConsignmentLinePermissions(effectiveReadOnly, statusCode, hasFirstReceive, permissions?.roles || []);
      },
      [isReadOnly, permissions?.roles]
    )
  };
};
